import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDropDown } from '../../components/icons';
import { SelectObject } from '../../types/certificates';
import Checkbox from '../Checkbox/Checkbox';
import cn from '../cn';
import { getPopoverPositionStyle } from '../popoverPosition';
import { useAnchorRect } from '../useAnchorRect';

type SelectMultipleProps = {
  selectedItems?: number[],
  selectedItemsString?: string[],
  width: number,
  widthPercentage?: boolean,
  filter_by_text: string,
  options?: string[],
  optionsObject?: SelectObject[],
  onSelectedItem: Function,
  customSelectedText?: string,
  required?: boolean,
  disabled?: boolean,
};

// MUI multi-<Select> port: checkbox menu, stays open on toggle, trigger shows
// "N items selected". Emits onSelectedItem({ target: { value: newArray } }) with
// the toggled array (ids or strings), mirroring the old wrapper's value logic.
const SelectMultiple: FunctionComponent<SelectMultipleProps> = React.memo(({
  selectedItems = [],
  required = false,
  selectedItemsString = [],
  width,
  filter_by_text,
  options,
  optionsObject,
  onSelectedItem,
  widthPercentage = false,
  customSelectedText,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const containerStyle = widthPercentage === false
    ? { minWidth: width + 'px', maxWidth: width + 'px' }
    : { width: width + '%' };

  const usingStrings = selectedItemsString.length > 0;
  const current: (string | number)[] = selectedItems.length > 0 ? selectedItems : selectedItemsString;
  const count = selectedItems.length > 0 ? selectedItems.length : selectedItemsString.length;
  const summary = count > 0 ? `${count}${customSelectedText !== undefined ? customSelectedText : ' items selected'}` : '';

  // Live rect: follows the anchor when a dialog body or the page scrolls.
  const rect = useAnchorRect(open, rootRef);

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      if (listRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDocDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const toggle = (value: string | number) => {
    const exists = current.some((x) => x === value);
    const next = exists ? current.filter((x) => x !== value) : [...current, value];
    onSelectedItem({ target: { value: next } });
  };

  type Row = { value: string | number; label: string; checked: boolean };
  const rows: Row[] = options !== undefined
    ? options.map((item) => ({ value: item, label: item, checked: usingStrings ? selectedItemsString.includes(item) : false }))
    : (optionsObject ?? []).map((item) => ({
        value: usingStrings ? item.name : item.id,
        label: item.name,
        checked: usingStrings ? selectedItemsString.indexOf(item.name) > -1 : selectedItems.indexOf(item.id) > -1,
      }));

  const popoverPosition = rect ? getPopoverPositionStyle(rect) : null;

  return (
    <div ref={rootRef} style={containerStyle} className="ds-control-root relative inline-flex flex-col align-top">
      <label className="ds-label">
        {filter_by_text}{required ? ' *' : ''}
      </label>

      <div
        role="combobox"
        aria-expanded={open}
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={cn('ds-field flex items-center justify-between',
          disabled ? 'cursor-default text-black/38' : 'cursor-pointer')}
      >
        <span className="truncate">{summary}</span>
        <ArrowDropDown className="text-black/54" />
      </div>

      {open && rect && createPortal(
        <ul
          ref={listRef}
          role="listbox"
          aria-multiselectable
          style={popoverPosition!.style}
          className={cn('ds-menu scrollbar-thin', popoverPosition!.className)}
        >
          {rows.map((r, i) => (
            <li
              key={i}
              role="option"
              aria-selected={r.checked}
              onClick={() => toggle(r.value)}
              className="ds-menu-item"
            >
              <Checkbox color="primary" size="small" checked={r.checked} />
              <span>{r.label}</span>
            </li>
          ))}
        </ul>,
        document.getElementById('root') ?? document.body,
      )}
    </div>
  );
});

export default SelectMultiple;
